import Text from '../../../../elements/Typo/Text'
import AOS from 'aos'
import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts'
import { convertToKFormat } from '../../../../_common/functions'
import { getChartColor, TestColorCode } from '../../../../_config'

interface BlockingChartProps {
    title: string
    subTitle?: string
    data: any[]
    className?: string
    yaxisTitle1?: string
    yaxisTitle2?: string
    xaxisTitle?: string
    toDownloadAsReport?: boolean
    selectedConfig?: string
}

interface CustomizedLabelProps {
    x: number
    y: number
    stroke: string
    value: number
    viewBox: { width: number }
}

export const convertValueToSingleValue = (value: any, selectedConfig?: string) => {
    let title = value?.split('_')?.[0]
    if (title === 'Uplift') {
        title = 'Uplift %'
    }

    if (selectedConfig == 'synthetic_control' && title === 'Control') {
        title = 'Synthetic Control'
    }
    return title
}

const getValueLabel = (value: any, selectedConfig?: string, t?: any) => {
    const converted = convertValueToSingleValue(value, selectedConfig) === 'Counterfactual' ? 'Counterfactual' : convertValueToSingleValue(value, selectedConfig)
    return t ? t(converted) : converted
}

export const CustomTooltip = ({ active, payload, label, graphColors, selectedConfig, t: translate }: any) => {
    const t = typeof translate === 'function' ? translate : (value: string) => value
    let isNA: boolean = false
    for (const group of payload) {
        if (typeof group.payload[group.dataKey] === 'string') {
            isNA = true
            break
        }
    }
    const takeFunc = (str: any) => {
        if (typeof str != 'string') {
            if (str >= 0) {
                return convertToKFormat(str)
            } else {
                return `-${convertToKFormat(str)}`
            }
        }
        return 'NA*'
    }
    if (active && payload.length) {
        return (
            <div className="bg-white shadow-2xl rounded-[0.667rem] p-3 text-center">
                <Text size="xl" className="flex-grow text-[#74788D] font-semibold py-2">
                    {label}
                </Text>
                <div className="flex flex-col bg-gray-100 gap-4 px-2">
                    {payload.map((group: any, i: any) => {
                        return (
                            <div className="flex items-center gap-2 justify-center" key={`tooltip-${group.dataKey}`}>
                                <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: getChartColor(getValueLabel(group?.name, selectedConfig, t), 0) }}></div>
                                <Text size="lg" className="flex-grow text-left text-[#74788D] mr-4">
                                    {getValueLabel(group?.name, selectedConfig, t)}
                                </Text>
                                <Text size="xxl" className="font-semibold">
                                    {takeFunc(group.payload[group.dataKey])}
                                </Text>
                            </div>
                        )
                    })}
                    <div className="flex w-full gap-2">
                        <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: graphColors[2] }}></div>
                        <Text size="lg" className="flex-grow text-left text-[#74788D] mr-4">
                            {t('P Value')}
                        </Text>
                        <Text size="xxl" className="font-semibold">
                            {payload?.[0]?.payload?.['P_Value'] >= 0 ? convertToKFormat(payload?.[0]?.payload?.['P_Value']) : '-' + convertToKFormat(payload?.[0]?.payload?.['P_Value'])}
                        </Text>
                    </div>
                    {isNA && (
                        <div className="mt-2 bg-gray-300 rounded-[0.2rem] w-full p-1">
                            <p className="text-start text-[0.75rem] w-[3/4]">
                                {t('*Uplift is NA because experiments status')}
                                <br />
                                {t('is No Impact / Inconclusive.')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return null
}

// Define a separate LegendContent component
export const LegendContent = ({ payload, graphColors, selectedConfig, t: translate }: any) => {
    const t = typeof translate === 'function' ? translate : (value: string) => value
    return (
        <div className="flex items-center justify-center gap-2 border">
            {payload?.map((entry: any, i: any) => (
                <p className="text-[0.583rem] text-gray-700 font-semibold flex justify-center items-center gap-2" key={`${i}-legend-${entry?.dataKey}`}>
                    <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: getChartColor(getValueLabel(entry?.dataKey, selectedConfig, t), 0) }}></div>
                    {getValueLabel(entry?.dataKey, selectedConfig, t)}
                </p>
            ))}
        </div>
    )
}

const CustomizedLabel: React.FC<CustomizedLabelProps> = ({ x, y, stroke, value, viewBox }) => {
    let v: any = value
    if (typeof value != 'string') {
        if (value >= 0) {
            v = convertToKFormat(value)
        } else {
            v = '-' + convertToKFormat(value)
        }
    }
    return (
        <text x={x + viewBox.width / 2} y={y} dx={0} dy={-4} fill={stroke} fontSize={10} fontWeight={500} textAnchor="middle">
            {v}
        </text>
    )
}

const BlockingChart: React.FC<BlockingChartProps> = ({ title = 'heading', subTitle = '', data, className = '', xaxisTitle = '', yaxisTitle1 = '', yaxisTitle2 = '', toDownloadAsReport = false, selectedConfig = '' }) => {
    const { t } = useTranslation()
    const graphColors: any = ['#E5C511', '#D69E77', '#D58E33']
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [chartWidth, setChartWidth] = useState(0)

    const getDefaultBarWidth = (rem: number) => {
        if (globalThis.window === undefined) {
            return rem * 16 // Assume default 16px root font size
        }
        const fontSizeStr = getComputedStyle(document.documentElement).fontSize // e.g. "16px"
        const rootFontSize = Number.parseFloat(fontSizeStr) // Converts "16px" -> 16
        return rem * rootFontSize
    }

    // Calculate minimum width needed for non-overlapping bars
    const barGapWidth = 3 // Gap between bars in the same group
    const categoryGapWidth = 10 // Gap between different data points/categories
    const barWidth = getDefaultBarWidth(1.708) // Width in pixels for each individual bar

    const calculateRequiredWidth = () => {
        const groupCount = data.length
        const barCountPerGroup = 2
        const barCount = barCountPerGroup * groupCount

        // Calculate the total width needed for all bars without overlapping
        const requiredWidth = barCount * barWidth + barCountPerGroup * barGapWidth * groupCount + (groupCount - 1) * categoryGapWidth
        setChartWidth(requiredWidth)
    }

    console.log('Blocking Factors Chart Title:', title)
    console.log('Blocking Factors Chart Data:', data)

    useEffect(() => {
        calculateRequiredWidth()
    }, [containerWidth, data])

    useEffect(() => {
        if (scrollContainerRef.current) {
            // Get the width of the parent container
            setContainerWidth(scrollContainerRef.current.clientWidth)
        }

        // Add resize listener
        const handleResize = () => {
            if (scrollContainerRef.current) {
                setContainerWidth(scrollContainerRef.current.clientWidth)
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        const params: any = {}

        if (toDownloadAsReport) {
            params['disable'] = true // Animation disabled
        } else {
            params['once'] = false // Animation occurs every time you scroll to the element
        }

        AOS.init(params)
        AOS.refresh()
    }, [])

    return (
        <>
            {data?.length ? (
                <div className={`border border-[#0000001A] rounded-[0.625rem] p-2 overflow-x-scroll ${className}`} data-aos="zoom-in" data-aos-delay="100" ref={scrollContainerRef}>
                    <div className="flex items-center justify-between mb-2">
                        <Text size="lg" className="font-semibold">
                            {title}
                            <br />
                            {subTitle}
                        </Text>
                    </div>
                    <div className="w-full h-[18.542rem] text-[0.5rem] flex relative" style={{ width: `${chartWidth}px`, minWidth: '100%' }}>
                        <div className="w-[1rem] flex items-center justify-center">
                            <p className="text-[0.5rem] text-center leading-none font-semibold -rotate-90 whitespace-nowrap">{yaxisTitle1}</p>
                        </div>
                        <ResponsiveContainer width="100%" height="100%" className="flex-grow">
                            <ComposedChart width={chartWidth} barGap={barGapWidth} height={400} data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                <Legend wrapperStyle={{ fontSize: '0.583rem', letterSpacing: '0.02em' }} content={<LegendContent graphColors={graphColors} selectedConfig={selectedConfig} t={t} />} />
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="Blocking_Factor_Value" stroke="#939393" />
                                <YAxis yAxisId="left" axisLine={false} strokeWidth={1} width={25} />
                                <YAxis overlineThickness={1} yAxisId="right" orientation="right" dataKey="Uplift_Percentage" width={25} />
                                <Tooltip content={<CustomTooltip graphColors={graphColors} selectedConfig={selectedConfig} t={t} />} cursor={{ fill: 'transparent' }} />

                                <Bar
                                    dataKey={data?.[0]?.['Counterfactual_Avg'] == undefined ? 'Control_Avg' : 'Counterfactual_Avg'}
                                    fill={getChartColor(getValueLabel(data?.[0]?.['Counterfactual_Avg'] == undefined ? 'Control_Avg' : 'Counterfactual_Avg', selectedConfig, t), 0)}
                                    radius={[7, 7, 0, 0]}
                                    barSize={barWidth}
                                    label={{
                                        position: 'top',
                                        content: <CustomizedLabel x={0} y={0} stroke={''} value={0} viewBox={{ width: 0 }} />,
                                    }}
                                    isAnimationActive={!toDownloadAsReport}
                                    yAxisId="left"
                                    style={{ cursor: 'default' }}
                                    activeBar
                                />

                                <Bar
                                    dataKey="Test_Avg"
                                    fill={TestColorCode}
                                    radius={[7, 7, 0, 0]}
                                    barSize={barWidth}
                                    label={{
                                        position: 'top',
                                        content: <CustomizedLabel x={0} y={0} stroke={''} value={0} viewBox={{ width: 0 }} />,
                                    }}
                                    isAnimationActive={!toDownloadAsReport}
                                    yAxisId="left"
                                    className="table-bars"
                                />
                                <Line
                                    type={'monotone'}
                                    dataKey="Uplift_Percentage"
                                    fill={graphColors[0]}
                                    stroke="transparent"
                                    yAxisId="right"
                                    label={{
                                        position: 'top',
                                        content: <CustomizedLabel x={0} y={0} stroke={''} value={0} viewBox={{ width: 0 }} />,
                                    }}
                                    isAnimationActive={!toDownloadAsReport}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                        {yaxisTitle2 && (
                            <div className="w-[1rem] flex items-center justify-center">
                                <p className="text-[0.5rem] text-center leading-none font-semibold rotate-90 whitespace-nowrap">{yaxisTitle2}</p>
                            </div>
                        )}
                    </div>
                    {xaxisTitle && <p className="text-[0.5rem] text-center leading-none font-semibold  whitespace-nowrap">{xaxisTitle}</p>}
                </div>
            ) : null}
        </>
    )
}

export default BlockingChart
